import { getActionDetail } from '@/services/actionLibrary';

const actionNameRequests = new Map();

export const RECORD_STATUS_LABELS = {
  generating: '评估生成中',
  succeeded: '评估已生成',
  failed: '评估生成失败',
  not_generated: '尚未生成评估',
};

const METRIC_LABELS = {
  validUnitCount: '完成动作次数',
  durationMs: '训练持续时间',
  totalFrameCount: '采集画面数',
  validFrameCount: '有效分析画面数',
  confidence: '分析可信度',
  actualFps: '实际分析帧率',
  avg_rom: '平均活动范围',
  jointRom: '关节活动范围',
  minAngle: '最小关节角度',
  maxAngle: '最大关节角度',
  targetSignalRange: '目标动作范围',
  normalizedMaxDisplacement: '最大动作幅度',
  cadence: '动作频率',
  phaseCompleteness: '动作完成度',
  phaseConsistency: '动作阶段一致性',
  cycleDurationCv: '动作节奏波动',
  eccentricDuration: '离心阶段时长',
  concentricDuration: '向心阶段时长',
  eccentricConcentricDurationRatio: '离心与向心时长比',
  concentricMinAngularVelocity: '向心阶段最低角速度',
  bodyCenterSwayX: '身体左右晃动',
  bodyCenterSwayY: '身体上下晃动',
  trunkTiltStd: '躯干倾斜波动',
  trunkTiltRange: '躯干晃动幅度',
  postureScore: '动作姿态评分',
  leftRightTimingDiff: '左右动作时序差',
  phaseSync: '左右动作同步度',
  peakAngularVelocity: '峰值角速度',
  peakAcceleration: '峰值角加速度',
  actionObservationCount: '已纳入评估的动作实例',
  phaseCount: '已纳入评估的训练阶段',
  actionTypeCount: '已纳入评估的动作种类',
  completedAt: '课程完成时间',
};

export function success(response) {
  return response && (response.code === '00000' || response.code === '0');
}

export async function loadActionNames(actionIds) {
  const uniqueIds = [...new Set(actionIds)].filter(Boolean);
  const entries = await Promise.all(
    uniqueIds.map(async (actionId) => {
      if (!actionNameRequests.has(actionId)) {
        const request = getActionDetail({
          requestData: { actionId },
        })
          .then((response) => {
            if (!success(response)) {
              actionNameRequests.delete(actionId);
              return '';
            }
            const action = response?.data?.action;
            return (
              action?.shortName || action?.actionName || action?.name || ''
            );
          })
          .catch(() => {
            actionNameRequests.delete(actionId);
            return '';
          });
        actionNameRequests.set(actionId, request);
      }
      return [actionId, await actionNameRequests.get(actionId)];
    }),
  );
  return Object.fromEntries(entries);
}

export function parseJson(value) {
  if (!value || typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

export function timeText(value) {
  if (value === null || value === undefined || value === '') return '-';
  const text = String(value).trim();
  const normalized = /^\d+$/.test(text) ? Number(text) : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

export function metricName(item) {
  if (item?.displayName && /[\u4e00-\u9fa5]/.test(item.displayName)) {
    return item.displayName.replace(/动作 \d+ · 实例 \d+ · /, '');
  }
  return METRIC_LABELS[item?.metricCode] || '训练观察指标';
}

function roundedNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number * 100) / 100 : value;
}

export function metricValue(value, unit, metricCode) {
  if (value === null || value === undefined || value === '') return '-';
  if (unit === 'epoch_ms') return timeText(value);
  if (Array.isArray(value)) return `${value.length} 项记录`;
  if (typeof value === 'object') return '已记录';
  const number = Number(value);
  if (unit === 'ratio' && Number.isFinite(number)) {
    if (metricCode?.endsWith('DurationRatio')) {
      return `${roundedNumber(number)} : 1`;
    }
    return `${Math.round(number * 1000) / 10}%`;
  }
  if (unit === '%' || unit === 'percent' || unit === 'percentage') {
    return `${roundedNumber(value)}%`;
  }
  if (unit === 'ms' && Number.isFinite(number)) {
    return number >= 1000
      ? `${Math.round(number / 100) / 10} 秒`
      : `${roundedNumber(number)} 毫秒`;
  }
  if (unit === 'count') {
    const countUnitLabels = {
      totalFrameCount: '帧',
      validFrameCount: '帧',
      actionObservationCount: '个动作',
      phaseCount: '个阶段',
      actionTypeCount: '种动作',
    };
    return `${roundedNumber(value)} ${countUnitLabels[metricCode] || '次'}`;
  }
  const unitLabels = {
    deg: '°',
    degree: '°',
    degrees: '°',
    'deg/s': '°/秒',
    'degree/s': '°/秒',
    'deg/s2': '°/秒²',
    'degree/s2': '°/秒²',
    'deg/s²': '°/秒²',
    'degree/s²': '°/秒²',
    'count/min': '次/分钟',
    fps: '帧/秒',
  };
  const label = unitLabels[unit] || '';
  const displayValue = Number.isFinite(number) ? roundedNumber(number) : value;
  return `${displayValue}${label ? ` ${label}` : ''}`;
}

export function observationValue(item) {
  if (Array.isArray(item?.series)) {
    return `${item.series.length} 次动作记录`;
  }
  return metricValue(item?.value, item?.unit, item?.metricCode);
}

/**
 * 将 Layer 3 按“指标”组织的 series 转成页面按“动作次数/周期”组织的数据。
 * Observation 仍保持原协议不变，只调整管理端的展示视角。
 */
export function groupUnitSeriesByPosition(items = []) {
  const rows = new Map();
  items.forEach((observation) => {
    const series = Array.isArray(observation?.series) ? observation.series : [];
    series.forEach((item, index) => {
      const position = Number(item?.position) || index + 1;
      const rowKey = item?.unitRef || `position-${position}`;
      if (!rows.has(rowKey)) {
        rows.set(rowKey, {
          key: rowKey,
          position,
          unitRef: item?.unitRef || '',
          sourceUnitValid: item?.sourceUnitValid !== false,
          metrics: [],
        });
      }
      const row = rows.get(rowKey);
      row.sourceUnitValid =
        row.sourceUnitValid && item?.sourceUnitValid !== false;
      row.metrics.push({
        observationId: observation?.observationId || '',
        metricCode: observation?.metricCode || '',
        name: metricName(observation),
        status: item?.status || 'not_generated',
        value:
          item?.status === 'available'
            ? metricValue(
                item.value,
                item.unit || observation?.unit,
                observation?.metricCode,
              )
            : '未生成',
      });
    });
  });
  return [...rows.values()].sort(
    (left, right) => left.position - right.position,
  );
}

/** 去掉模型偶尔写进正文的内部 evidenceId 标记，证据关系仍保留在 evidenceRefs。 */
export function readableInterpretationText(value) {
  if (typeof value !== 'string') return value;
  return value
    .replace(/\[\s*evidence(?:Id)?\s*:\s*[^\]]+\]/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function groupObservationsByCategory(items = []) {
  return items.reduce((groups, item) => {
    const categories =
      Array.isArray(item?.categoryRefs) && item.categoryRefs.length
        ? [...new Set(item.categoryRefs)]
        : ['other_features'];
    categories.forEach((category) => {
      if (!groups[category]) groups[category] = [];
      groups[category].push(item);
    });
    return groups;
  }, {});
}

export function narrativeStatements(value) {
  const parsed = parseJson(value);
  if (Array.isArray(parsed?.statements)) {
    return parsed.statements
      .map((item) => (typeof item === 'string' ? item : item?.text))
      .filter(Boolean);
  }
  if (typeof value === 'string' && value.trim() && !parsed) return [value];
  return [];
}
